import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { IRegulation } from '../contracts/amendment.contract';
import { ICalendarRiskGroup } from '../contracts/calendar-risk-group.contract';
import { ICalendarSurveyGroup } from '../contracts/calendar-survey-group.contract';
import { ICalendarSurvey } from '../contracts/calendar-survey.contract';
import { IVessel } from '../contracts/vessel.contract';
import { CalendarService } from '../services';
import { RootState } from './store.redux';

interface IInitialState {
    List: {
        [key: string]:
            | {
                  CalendarGroup: ICalendarSurveyGroup[] | undefined;
                  IsLoading: boolean;
                  ErrorMessage: string | undefined;
              }
            | undefined;
    };
    Regulations: {
        List: IRegulation[] | undefined;
        IsLoading: boolean;
        ErrorMessage: string | undefined;
    };
    Filters: {
        Regulations: IRegulation[] | undefined;
        Vessels: IVessel[] | undefined;
        Surveys: ICalendarSurvey[] | undefined;
    };
    Report: {
        IsLoading: boolean;
    };
    Risk: {
        List: ICalendarRiskGroup[];
        IsLoading: boolean;
        ErrorMessage: string | undefined;
    };

    AxiosController: AbortController;
}

const initialState: IInitialState = {
    List: {},
    Regulations: {
        List: undefined,
        IsLoading: false,
        ErrorMessage: undefined
    },
    Filters: {
        Regulations: [],
        Vessels: [],
        Surveys: []
    },
    Report: {
        IsLoading: false
    },
    Risk: {
        List: [],
        IsLoading: false,
        ErrorMessage: undefined
    },
    AxiosController: new AbortController()
};

const Fetch = {
    GetByClassNumberYear: createAsyncThunk('calendar/GetByClassNumberYear', async ({ classNumber, maxYear }: { classNumber: string; maxYear: number }, { rejectWithValue, getState }) => {
        try {
            const rootState = getState() as RootState;
            const res = await CalendarService.getByClassNumberYear(classNumber, maxYear, rootState.Calendar.AxiosController);
            return { classNumber, res };
        } catch (err) {
            return rejectWithValue(err);
        }
    }),
    GetRegulationsByClassNumber: createAsyncThunk('calendar/GetRegulationsByClassNumber', async (classNumbers: string[], { rejectWithValue, getState }) => {
        try {
            const rootState = getState() as RootState;
            const res = await CalendarService.getRegulationsByClassNumber(classNumbers, rootState.Calendar.AxiosController);
            return res;
        } catch (err) {
            return rejectWithValue(err);
        }
    }),
    GetRiskByClassNumberYear: createAsyncThunk('calendar/GetRiskByClassNumberYear', async ({ classNumbers, maxYear }: { classNumbers: string[]; maxYear: number }, { rejectWithValue, getState }) => {
        try {
            const rootState = getState() as RootState;
            const res = await CalendarService.getRiskByClassNumberYear(classNumbers, maxYear, rootState.Calendar.AxiosController);
            return res;
        } catch (err) {
            return rejectWithValue(err);
        }
    }),
    DownloadCalendarReport: createAsyncThunk(
        'calendar/DownloadCalendarReport',
        async ({ type, fileExtension, imoNumbers, showGuidelines }: { type: string; fileExtension: string; imoNumbers: string[]; showGuidelines: boolean }, { rejectWithValue }) => {
            try {
                const res = await CalendarService.downloadCalendarReport(type, fileExtension, imoNumbers, showGuidelines);
                return res;
            } catch (err) {
                return rejectWithValue(err);
            }
        }
    )
};

const Slice = createSlice({
    name: 'calendar',
    initialState: initialState,
    reducers: {
        AddFilterRegulation: (state, action: PayloadAction<IRegulation[]>) => {
            const newRegulations = action.payload.filter((r) => !state.Filters.Regulations?.some((reg) => reg.RegulationName === r.RegulationName));
            state.Filters.Regulations = state.Filters.Regulations?.concat(newRegulations);
        },
        RemoveFilterRegulation: (state, action: PayloadAction<IRegulation>) => {
            state.Filters.Regulations = state.Filters.Regulations?.filter((r) => r.RegulationName !== action.payload.RegulationName);
        },
        ClearFilterRegulations: (state) => {
            state.Filters.Regulations = [];
        },
        CancelApiRequests: (state) => {
            state.AxiosController.abort();
            state.AxiosController = new AbortController();
        },

        AddFilterVessel: (state, action: PayloadAction<IVessel[]>) => {
            const newVessels = action.payload.filter((v) => !state.Filters.Vessels?.some((ves) => ves.ImoNumber === v.ImoNumber));
            state.Filters.Vessels = state.Filters.Vessels?.concat(newVessels);
        },
        RemoveFilterVessel: (state, action: PayloadAction<IVessel>) => {
            state.Filters.Vessels = state.Filters.Vessels?.filter((v) => v.ImoNumber !== action.payload.ImoNumber);
        },
        ClearFilterVessels: (state) => {
            state.Filters.Vessels = [];
        },
        ClearFilterSurveys: (state) => {
            state.Filters.Surveys = [];
        },
        ClearRisks: (state) => {
            state.Risk.List = [];
        },
        // Add and remove filters for surveys
        AddFilterSurvey: (state, action: PayloadAction<ICalendarSurvey[]>) => {
            state.Filters.Surveys = state.Filters.Surveys?.concat(action.payload);
        },
        RemoveFilterSurvey: (state, action: PayloadAction<ICalendarSurvey>) => {
            state.Filters.Surveys = state.Filters.Surveys?.filter(
                (s) => !(s.ClassNumber === action.payload.ClassNumber && s.SurveyDescription === action.payload.SurveyDescription && s.SurveyDueDate === action.payload.SurveyDueDate)
            );
        }
    },
    extraReducers: (builder) => {
        // GetByClassNumberYear
        builder.addCase(Fetch.GetByClassNumberYear.pending, (state, action: any) => {
            state.List[`${action.meta.arg.classNumber}`] = {
                CalendarGroup: undefined,
                IsLoading: true,
                ErrorMessage: undefined
            };
        });
        builder.addCase(Fetch.GetByClassNumberYear.fulfilled, (state, action: PayloadAction<{ classNumber: string; res: ICalendarSurveyGroup[] }>) => {
            const { classNumber } = action.payload;
            state.List[`${classNumber}`] = {
                CalendarGroup: action.payload.res,
                IsLoading: false,
                ErrorMessage: undefined
            };
        });
        builder.addCase(Fetch.GetByClassNumberYear.rejected, (state, action: any) => {
            const { classNumber, year, res } = action.meta.arg;
            state.List[`${year}-${classNumber}`] = {
                CalendarGroup: undefined,
                IsLoading: false,
                ErrorMessage: res
            };
        });
        // GetRegulationsByClassNumber
        builder.addCase(Fetch.GetRegulationsByClassNumber.pending, (state, action) => {
            state.Regulations.IsLoading = true;
            state.Regulations.ErrorMessage = undefined;
        });
        builder.addCase(Fetch.GetRegulationsByClassNumber.fulfilled, (state, action: PayloadAction<IRegulation[] | undefined>) => {
            if (!action.payload) return;

            // Merge new regulations with existing regulations and remove duplicates based on a unique property (e.g., id)
            const existingRegulations = state.Regulations.List ?? [];
            const newRegulations = action.payload ?? [];
            const combinedRegulations = [...existingRegulations, ...newRegulations];

            const uniqueRegulationsMap = new Map<string, IRegulation>();
            combinedRegulations.forEach((regulation) => {
                uniqueRegulationsMap.set(`${regulation.RegulationName}_${regulation.RegulationDocName}`, regulation); // RegualtionName && RegulationDocName the unique property
            });

            state.Regulations.List = Array.from(uniqueRegulationsMap.values());
            state.Regulations.IsLoading = false;
            state.Regulations.ErrorMessage = undefined;
        });
        builder.addCase(Fetch.GetRegulationsByClassNumber.rejected, (state, action) => {
            state.Regulations.IsLoading = false;
            state.Regulations.ErrorMessage = action.payload?.toString();
        });
        // GetRiskByClassNumberYear
        builder.addCase(Fetch.GetRiskByClassNumberYear.pending, (state, action: any) => {
            state.Risk.IsLoading = true;
            state.Risk.List = [];
            state.Risk.ErrorMessage = undefined;
        });
        builder.addCase(Fetch.GetRiskByClassNumberYear.fulfilled, (state, action: PayloadAction<ICalendarRiskGroup[]>) => {
            state.Risk.IsLoading = false;
            state.Risk.List = action.payload ?? [];
            state.Risk.ErrorMessage = undefined;
        });
        builder.addCase(Fetch.GetRiskByClassNumberYear.rejected, (state, action: any) => {
            state.Risk.IsLoading = false;
            state.Risk.List = [];
            state.Risk.ErrorMessage = action.meta.arg.res;
        });
        // DownloadCalendarReport
        builder.addCase(Fetch.DownloadCalendarReport.pending, (state) => {
            state.Report.IsLoading = true;
        });
        builder.addCase(Fetch.DownloadCalendarReport.fulfilled, (state) => {
            state.Report.IsLoading = false;
        });
        builder.addCase(Fetch.DownloadCalendarReport.rejected, (state) => {
            state.Report.IsLoading = false;
        });
    }
});

export const CalendarFetch = Fetch;
export const CalendarAction = Slice.actions;
export const CalendarReducer = Slice.reducer;
